/* eslint-disable @next/next/no-img-element */
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import replace from 'lodash/replace';
import { fetchS3SignInUrl } from '@actions/others';
import {
  updateDiscussionForumText,
  updateDiscussionFormStory
} from '@actions/educator';
import Attachment from '@icon/Attachment';
import S3Uploader from '@base/S3Uploader/S3Uploader';
import TextField from '@base/TextField/TextField';
import { useDispatchToStore, useSelectorToStore } from '@hooks/reduxHooks';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import Skeleton from '@material-ui/lab/Skeleton';
import P1 from '@base/P1/P1';
import H6 from '@base/H6/H6';
import H7 from '@base/H7/H7';
import Close from '@icon/Close';
import Pdf from '@icon/Pdf';
import Pgn from '@icon/Pgn';
import Button from '@base/Button/Button';
import { fetchCourseUpdates } from '@actions/courses';
import { Fonts, Spacings } from '@styles/index';
import useEducatorContext from '@hooks/useEducatorContext';

const {
  SPACING_7B,
  SPACING_6B,
  SPACING_4B,
  SPACING_2B,
  SPACING_3B,
  SPACING_1B
} = Spacings;

const UploadingDiv = styled.div`
  width: 416px;
  height: 104px;
  border: 1px solid var(--color-divider);
  border-radius: ${SPACING_2B};
  display: flex;
  padding: ${SPACING_7B} ${SPACING_7B} ${SPACING_7B} ${SPACING_6B};
  margin-top: ${SPACING_4B};
  justify-content: space-between;
  cursor: pointer;
  transition: box-shadow 200ms ease-in-out, transform 200ms ease-in-out;
  &:hover {
    box-shadow: 0px 16px 32px 0px rgba(221, 230, 237, 0.4);
    transform: translate(0px, -2px);
  }
`;
const LeftDiv = styled.div`
  display: flex;
`;
const ImageDiv = styled.div`
  margin-right: ${SPACING_4B};
`;
const FileNameDiv = styled.div`
  width: 190px;
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const RightDiv = styled.div`
  align-items: center;
  display: flex;
  margin-left: ${SPACING_2B};
  cursor: pointer;
`;

const Input = styled(TextField)`
  &.MuiInputBase-root {
    width: ${({ width }) => width || '416px'};
    padding: 0px;
    .MuiInputBase-input {
      padding: ${SPACING_4B} ${SPACING_6B};
    }
    .MuiInputBase-input::placeholder {
      font-size: ${Fonts.H6};
      color: var(--color-text-secondary);
      font-weight: ${Fonts.SEMIBOLD};
    }
    .MuiOutlinedInput-notchedOutline {
      border-color: ${({ isInvalid }) => isInvalid && 'var(--color-i-red)'};
    }
  }
`;

const Wrapper = styled.div`
  grid-column: span 10;
  background-color: var(--color-i-white);
  border-radius: ${SPACING_2B};
  box-shadow: 0px 16px 32px rgba(221, 230, 237, 0.4);
  padding: ${SPACING_6B};
  margin-top: ${SPACING_6B};
`;

const ButtonDiv = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${SPACING_4B};
`;

const ErrorDiv = styled(H7)`
  color: var(--color-i-red);
`;

const AttachDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${SPACING_3B} ${SPACING_7B} ${SPACING_3B} ${SPACING_7B};
  width: 128px;
  height: 40px;
  background: var(--color-base-1);
  border: 1px solid var(--color-divider);
  border-radius: ${SPACING_1B};
  &:hover {
    box-shadow: ${boxShadowGenerator()};
    transform: translate(0px, -2px);
    border-color: var(--color-i-blue);
    cursor: pointer;
  }
  &:active {
    border: ${({ variant }) => variant !== 'hollow' && 'none'};
    box-shadow: ${boxShadowGenerator({
      yOffset: SPACING_2B,
      blur: SPACING_2B
    })};
    background-color: var(--color-base-1);
  }
  transition: box-shadow 200ms ease-in-out, transform 200ms ease-in-out;
`;

const isPGN = (name) => name.substr('-4') === '.pgn';

const DiscussionForum = ({ channelId }) => {
  const dispatch = useDispatchToStore();
  const isEducatorSide = useEducatorContext();

  const mapStateToProps = ({ educator }) => {
    return {
      discussionForum: educator.courseDetails.discussionForum
    };
  };

  const props = useSelectorToStore(mapStateToProps, false);
  const { discussionForum } = props;

  const { text: discussionText } = discussionForum;

  const reference = useRef();
  const accessRef = (input) => {
    reference.current = input;
  };

  const placeHolder = 'Share useful links and advice with your learners';
  const [isUploading, setIsUploading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [isUploadError, setErrorInUploading] = useState(false);
  const [uploadErrorMsg, setUploadErrorMsg] = useState(null);
  const [isPdf, setIsPdf] = useState(false);
  const [isImg, setIsImg] = useState(false);
  const [isPgn, setIsPgn] = useState(false);
  const [url, setUrl] = useState(null);
  const [name, setName] = useState(null);

  const IMAGE_FORMATS = ['image/png', 'image/jpeg', 'image/jpg'];

  const onChange = (e) => {
    dispatch(updateDiscussionForumText(e.target.value));
  };
  const onUploadStart = useCallback((data, next) => {
    const ispgn = isPGN(data.name);
    const modifiedData = new File([data], data.name, {
      type: ispgn ? 'application/x-chess-pgn' : data.type
    });
    setErrorInUploading(false);
    setIsPdf(false);
    setIsImg(false);
    setIsPgn(false);
    setName(modifiedData.name);
    if (ispgn) setIsPgn(true);
    if (modifiedData.type.includes('pdf')) setIsPdf(true);
    if (IMAGE_FORMATS.find((ele) => ele === modifiedData.type)) setIsImg(true);
    setIsUploading(true);
    setIsUploaded(false);
    next(modifiedData);
  }, []);

  const onUploadError = () => {
    setIsUploading(false);
    setIsUploaded(false);
    setErrorInUploading(true);
  };

  const onUploadFinish = useCallback((urlDetails) => {
    setUrl(urlDetails.publicUrl);
    setIsUploading(false);
    setIsUploaded(true);
  }, []);

  const removeFile = () => {
    setName(null);
    setUrl(null);
    setIsPdf(false);
    setIsImg(false);
    setErrorInUploading(false);
    reference.current.value = null;
  };

  const renderThumbNail = () => {
    if (!isPdf && !isPgn)
      return <img src={url} alt={name} width="83px" height="47px" />;
    if (isPgn) return <Pgn size="50px" viewBox="0 0 30 30" />;
    return <Pdf size="50px" />;
  };
  const loadMoreStories = () =>
    dispatch(fetchCourseUpdates(channelId, true, true));

  const getSignedUrl = useCallback((fileData, callback) => {
    const fileName = replace(fileData.name, new RegExp('&', 'g'), 'and');
    dispatch(fetchS3SignInUrl(fileName, 1)).then((res) => {
      if (res.error) {
        setErrorInUploading(true);
        setUploadErrorMsg(res.error.message);
        return;
      }
      const data = {
        signedUrl: res.result.signed_url,
        publicUrl: res.result.public_url
      };
      callback(data);
    });
  }, []);

  const handlePostClick = () => {
    const data = {};
    data.message = discussionText;
    data.object_type = 0;
    if (isImg) data.object_type = 1;
    if (isPdf || isPgn) data.object_type = 17;
    data.name = name;
    data.object_meta = url;
    dispatch(updateDiscussionFormStory(data, channelId)).then((res) => {
      if (res.result) {
        setName(null);
        dispatch(updateDiscussionForumText(''));
        setIsPdf(false);
        setIsImg(false);
        setUrl(null);
        loadMoreStories();
        reference.current.value = null;
      }
    });
  };

  if (!isEducatorSide) return null;
  return (
    <Wrapper>
      <Input
        type="text"
        placeholder={placeHolder}
        onChange={onChange}
        value={discussionText}
        multiline
        rows={5}
      />
      {name && (
        <UploadingDiv>
          <LeftDiv>
            <ImageDiv>
              {isUploading && (
                <Skeleton variant="rect" width="83px" height="47px" />
              )}
              {isUploaded && renderThumbNail()}
            </ImageDiv>
            <FileNameDiv>
              <P1>{` ${name}`}</P1>
              {isUploading && <P1>Uploading</P1>}
              {isUploadError && uploadErrorMsg && (
                <label htmlFor="uploadFile">
                  <ErrorDiv>
                    {uploadErrorMsg}
                    UPLOAD FAILED. TRY AGAIN
                  </ErrorDiv>
                </label>
              )}
            </FileNameDiv>
          </LeftDiv>
          <RightDiv>
            <Close size="12px" onClick={removeFile} />
          </RightDiv>
        </UploadingDiv>
      )}

      <ButtonDiv>
        <label htmlFor="uploadFile">
          <AttachDiv>
            <Attachment size="16px" />
            <H6>Attach</H6>
          </AttachDiv>
          <S3Uploader
            getSignedUrl={getSignedUrl}
            onUploadStart={onUploadStart}
            onUploadError={onUploadError}
            onUploadFinish={onUploadFinish}
            acceptedFiles="allFiles"
            accessRef={accessRef}
          />
        </label>
        <Button
          variant={discussionText ? 'filled' : 'disabled'}
          label="Post"
          color="blue"
          grid={2}
          width="93px"
          onClick={handlePostClick}
        />
      </ButtonDiv>
    </Wrapper>
  );
};

export default DiscussionForum;

DiscussionForum.propTypes = {
  channelId: PropTypes.string.isRequired,
  discussionForum: PropTypes.objectOf(PropTypes.any)
};
DiscussionForum.defaultProps = {
  discussionForum: {}
};
